import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Typography, Tag, Space, Descriptions, Result, Button } from "antd";
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { useIdDemand } from "../../hooks/useIdDemand";
import Loading from "../Loading";
import JsonViewer from "../../components/common/JsonViewer";
import { originLabels } from "../../constants/originLabels";

const { Title, Text } = Typography;

/**
 * Página para exibir o resultado da busca de fonte.
 *
 * Exibe os dados da fonte encontrada e a origem (API) onde foi encontrada.
 */
const GetIdDemandShowContent: React.FC = () => {
  const { getIdDemand } = useIdDemand();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<any>({});
  const [error, setError] = useState<string | null>(null);

  /**
   * Carrega os dados da fonte.
   */
  useEffect(() => {
    const getData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const res = await getIdDemand(id);
        setResponse(res);
      } catch (err: any) {
        console.error("Erro ao buscar fonte:", err);
        setError(err.message || "Erro ao buscar fonte");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [id, getIdDemand]);

  /**
   * Handler para voltar à página de busca.
   */
  const handleBack = () => {
    navigate("/buscar-fonte");
  };

  const origin = response.key || "not-found";
  const originLabel = originLabels[origin] || "Não encontrada";
  const found = origin !== "not-found" && response.data;
  const deleted = response.deleted === true;
  const isDatateamOrCodilodt = origin.startsWith("codilo-dt-") || origin.startsWith("datateam-");
  const frequencyType = response.frequencyType;
  const logged = response.logged;
  // Detecta se é autorequest
  const isAutorequest = origin === "autorequest" || response.isAutorequest === true;

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Erro ao buscar fonte"
        subTitle={<Text copyable>{error}</Text>}
        extra={[
          <Button type="primary" key="back" onClick={handleBack} icon={<ArrowLeftOutlined />}>
            Voltar
          </Button>,
          <Button key="retry" onClick={() => window.location.reload()}>
            Tentar novamente
          </Button>,
        ]}
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "18px" }}>
                {id}
              </Text>
              <Tag color={found ? "success" : "error"} icon={found ? <CheckCircleOutlined /> : <CloseCircleOutlined />} style={{ fontSize: "14px", padding: "4px 12px" }}>
                {originLabel}
              </Tag>
              {found && logged && (
                <Tag color="green">Logado</Tag>
              )}
            </div>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
          </div>

          {deleted && response.deletedDate && (
            <div style={{ marginTop: "8px" }}>
              <Tag color="error">Demanda deletada em {response.deletedDate}</Tag>
            </div>
          )}

          {found && (
            <div style={{ marginTop: "16px" }}>
              {/* Informações específicas para CapturaWeb - mostradas como tags */}
              {origin === "capturaweb" && (
                <>
                  <Space wrap size="small">
                    {response.platform && <Tag color="cyan">{response.platform}</Tag>}
                    {response.search && <Tag color="volcano">{response.search}</Tag>}
                    {response.param && <Tag color="purple">{response.param}</Tag>}
                    {response.paramKey && <Tag color="geekblue">{response.paramKey}</Tag>}
                  </Space>
                  {response.cwCounts && (
                    <div style={{ marginTop: "8px" }}>
                      <Space wrap size="small">
                        {response.cwCounts.cover > 0 && (
                          <Tag color="default">Cover: {response.cwCounts.cover}</Tag>
                        )}
                        {response.cwCounts.properties > 0 && (
                          <Tag color="default">Properties: {response.cwCounts.properties}</Tag>
                        )}
                        {response.cwCounts.people > 0 && (
                          <Tag color="default">People: {response.cwCounts.people}</Tag>
                        )}
                        {response.cwCounts.steps > 0 && (
                          <Tag color="default">Steps: {response.cwCounts.steps}</Tag>
                        )}
                      </Space>
                    </div>
                  )}
                </>
              )}

              {/* Informações específicas para Push */}
              {origin === "push" && (
                <>
                  {response.cnj && (
                    <div style={{ marginBottom: "8px" }}>
                      <Text strong>CNJ: </Text>
                      <Text copyable>{response.cnj}</Text>
                    </div>
                  )}
                  {response.infoCounts && (
                    <div style={{ marginBottom: "8px" }}>
                      <Space size={4} wrap>
                        {response.infoCounts.error > 0 && (
                          <Tag color="error" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Error: {response.infoCounts.error}</Tag>
                        )}
                        {response.infoCounts.warning > 0 && (
                          <Tag color="warning" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Warning: {response.infoCounts.warning}</Tag>
                        )}
                        {response.infoCounts.success > 0 && (
                          <Tag color="success" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Success: {response.infoCounts.success}</Tag>
                        )}
                        {response.infoCounts.pending > 0 && (
                          <Tag color="processing" style={{ fontSize: "11px", padding: "2px 6px", margin: 0 }}>Pending: {response.infoCounts.pending}</Tag>
                        )}
                        {/* Mostra se não há nenhum status encontrado */}
                        {response.infoCounts.error === 0 && 
                         response.infoCounts.warning === 0 && 
                         response.infoCounts.success === 0 && 
                         response.infoCounts.pending === 0 && (
                          <Text type="secondary" style={{ fontSize: "11px" }}>
                            Nenhum status encontrado no campo info
                          </Text>
                        )}
                      </Space>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {found && !deleted && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <Title level={4} style={{ margin: 0 }}>Dados da Fonte</Title>
                {isAutorequest && (response.id || response.data?.id) && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => {
                      const autorequestId = response.id || response.data?.id;
                      if (autorequestId) {
                        window.open(`/autorequest-details/${autorequestId}`, '_blank');
                      }
                    }}
                  >
                    Ver detalhes
                  </Button>
                )}
              </div>
              <JsonViewer src={response.data} name={origin} collapsed={false} />
            </div>
          )}
        </Space>
      </Card>
    </Space>
  );
};

/**
 * Componente principal da página GetIdDemandShow.
 */
const GetIdDemandShow: React.FC = () => {
  return (
    <GetIdDemandShowContent />
  );
};

export default GetIdDemandShow;
